import React from "react";
import { Box, Card, Grid, Skeleton, Stack } from "@mui/material";

/**
 * Loading placeholders for the admin console.
 *
 * These stand in for content that is about to arrive, so each one mirrors the
 * shape of the real thing (grid header + rows, card grid, tabbed form). A
 * skeleton that matches the final layout keeps the page from jumping when the
 * data lands, which a centred spinner cannot do.
 */

const shellSx = { borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" } as const;

// Deterministic width jitter, so the rows read as text rather than as a grid of
// identical bars — but stay stable across renders (no Math.random flicker).
const CELL_WIDTHS = ["70%", "45%", "60%", "35%", "55%", "50%"];

interface TableSkeletonProps {
  /** Body rows to draw. Roughly a screenful by default. */
  rows?: number;
  /** Columns to draw. Match the page's real column count for the closest fit. */
  columns?: number;
  /** Height of the block, to match the DataGrid container it replaces. */
  height?: number;
}

/** Stands in for a DataGrid: a header strip over evenly spaced text rows. */
export const TableSkeleton: React.FC<TableSkeletonProps> = ({ rows = 8, columns = 5, height = 560 }) => {
  const template = `repeat(${columns}, 1fr)`;

  return (
    <Card elevation={0} sx={{ ...shellSx, height }} role='status' aria-label='Loading data'>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: template,
          gap: "16px",
          px: "16px",
          py: "14px",
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "action.hover"
        }}
      >
        {Array.from({ length: columns }).map((_, c) => (
          <Skeleton key={c} variant='text' width='60%' sx={{ fontSize: "0.875rem" }} />
        ))}
      </Box>
      {Array.from({ length: rows }).map((_, r) => (
        <Box
          key={r}
          sx={{
            display: "grid",
            gridTemplateColumns: template,
            gap: "16px",
            px: "16px",
            py: "12px",
            borderBottom: "1px solid",
            borderColor: "divider"
          }}
        >
          {Array.from({ length: columns }).map((_, c) => (
            <Skeleton key={c} variant='text' width={CELL_WIDTHS[(r + c) % CELL_WIDTHS.length]} sx={{ fontSize: "1rem" }} />
          ))}
        </Box>
      ))}
    </Card>
  );
};

/** Stands in for a responsive grid of cards, matching the Organisations layout. */
export const CardGridSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <Grid container spacing={20} role='status' aria-label='Loading organisations'>
    {Array.from({ length: count }).map((_, i) => (
      <Grid item xs={12} sm={6} md={4} key={i}>
        <Card elevation={0} sx={{ ...shellSx, p: "20px" }}>
          <Stack direction='row' spacing={12} sx={{ alignItems: "center", mb: "16px" }}>
            <Skeleton variant='circular' width={44} height={44} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton variant='text' width='70%' sx={{ fontSize: "1.125rem" }} />
              <Skeleton variant='text' width='40%' sx={{ fontSize: "0.75rem" }} />
            </Box>
          </Stack>
          <Skeleton variant='text' width='95%' />
          <Skeleton variant='text' width='80%' />
          <Skeleton variant='rounded' height={32} sx={{ mt: "16px", borderRadius: 2 }} />
        </Card>
      </Grid>
    ))}
  </Grid>
);

/** Stands in for the config editor: a tab strip over stacked form fields. */
export const FormSkeleton: React.FC<{ fields?: number; tabs?: number }> = ({ fields = 6, tabs = 5 }) => (
  <Card elevation={0} sx={{ ...shellSx, p: "24px" }} role='status' aria-label='Loading configuration'>
    <Stack direction='row' spacing={24} sx={{ mb: "24px", pb: "12px", borderBottom: "1px solid", borderColor: "divider" }}>
      {Array.from({ length: tabs }).map((_, i) => (
        <Skeleton key={i} variant='text' width={80} sx={{ fontSize: "0.875rem" }} />
      ))}
    </Stack>
    <Stack spacing={20}>
      {Array.from({ length: fields }).map((_, i) => (
        <Box key={i}>
          <Skeleton variant='text' width={140} sx={{ fontSize: "0.75rem", mb: "6px" }} />
          <Skeleton variant='rounded' height={40} sx={{ borderRadius: 2 }} />
        </Box>
      ))}
    </Stack>
  </Card>
);
